import { FC, useState } from 'react'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import { useScoreGrid } from '../../../../graphql/score-grid/hooks/useScoreGrid'
import {
  FullMeetingWithEvaluationAndSelfReflectionFragment,
  MeetingStepStatus,
  MeetingStepViewId,
  ScoreGridDto,
  Country,
} from '../../../../types'
import { AppraisalContextType } from '../../AppraisalTypes'
import EvaluationContentContainer from '../../evaluation/EvaluationContentContainer'
import { EvaluationFooterComponent } from '../../evaluation/EvaluationFooterComponent'
import { EvaluationStepButtonsComponent } from '../../evaluation/EvaluationStepButtonsComponent'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { generateInitialState, allScoreGoalsExist } from '../../evaluation/EvaluationUtil'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingWithEvaluationAndSelfReflectionFragment
}

interface AppraisalMeetingStepEvaluationContentProps extends Props {
  currentGrid: ScoreGridDto
  isIdVWeightEnabled: boolean
}

const AppraisalMeetingStepEvaluation: FC<Props> = ({ meeting, backBtn, nextBtn }) => {
  const { data, loading, error } = useScoreGrid(meeting.id, meeting.employee.id, meeting.appraisalSeason.country as Country, true)

  if (loading || error || !data || !data.scoreGridWithHistory) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.scoreGridWithHistory} error={error} showSpinner />
  }

  const currentGrid = { ...data.scoreGridWithHistory }
  const isIdVWeightEnabled = data.isIdVWeightEnabled

  return (
    <AppraisalMeetingStepEvaluationContent
      isIdVWeightEnabled={isIdVWeightEnabled}
      currentGrid={currentGrid}
      meeting={meeting}
      backBtn={backBtn}
      nextBtn={nextBtn}
    />
  )
}

const AppraisalMeetingStepEvaluationContent: FC<AppraisalMeetingStepEvaluationContentProps> = ({
  meeting,
  backBtn,
  nextBtn,
  currentGrid,
  isIdVWeightEnabled,
}) => {
  const [evaluationStepStatus, setEvaluationStepStatus] = useState(MeetingStepStatus.Visited)
  const [goalsState, setGoalsState] = useState(generateInitialState(currentGrid.scoreGoals, AppraisalContextType.Meeting))
  const employeeName = meeting.employee.chosenFirstName ? meeting.employee.chosenFirstName : meeting.employee.legalFirstName
  const hasValidEmployeeScore = !!currentGrid.employeePreparationScore
  const showFooter =
    hasValidEmployeeScore ||
    !!currentGrid.lastResultScore ||
    (isIdVWeightEnabled && (!!currentGrid.resultScore || allScoreGoalsExist(goalsState, AppraisalContextType.Meeting)))

  return (
    <div data-e2e-appraisal-step-evaluation>
      <EvaluationContentContainer
        goalsState={goalsState}
        setGoalsState={setGoalsState}
        grid={currentGrid}
        meeting={meeting}
        stepStatus={evaluationStepStatus}
        type={AppraisalContextType.Meeting}
        onStatusChange={setEvaluationStepStatus}
        showFooter={showFooter}
      />

      <EvaluationFooterComponent
        meeting={meeting}
        isIdVWeightEnabled={isIdVWeightEnabled}
        goalsState={goalsState}
        currentGrid={currentGrid}
        type={AppraisalContextType.Meeting}
        evaluationScore={currentGrid.resultScore}
        employeeName={employeeName}
        employeePreparationPublic={true}
        showFooter={showFooter}
      />

      <EvaluationStepButtonsComponent meetingId={meeting.id} backBtn={backBtn} nextBtn={nextBtn} currentStepStatus={evaluationStepStatus} />
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Evaluation} stepStatus={evaluationStepStatus} />
    </div>
  )
}

export default AppraisalMeetingStepEvaluation
